import { type ReactNode } from "react";
import { Info } from "@phosphor-icons/react";
import { Card, Tooltip } from "../../design-system";
import { cn } from "../../design-system/utils/cn";

export interface PanelCardProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Short helper text shown behind an info icon next to the title. */
  help?: ReactNode;
  /** Right-aligned header slot (links, toggles, badges). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Remove body padding (e.g. for a chart that bleeds to the edges). */
  flush?: boolean;
}

/* The standard section container: a DS Card with a titled header, optional help
 * tooltip, and an action slot. Every panel on the dashboard uses this so
 * spacing, type, and dividers stay uniform. */
export function PanelCard({
  title,
  subtitle,
  help,
  action,
  children,
  className,
  bodyClassName,
  flush = false,
}: PanelCardProps) {
  return (
    <Card padding="none" className={cn("flex flex-col", className)}>
      <div className="flex items-start gap-3 px-5 pt-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate text-h6 font-semibold text-fg-1">
              {title}
            </h3>
            {help && (
              <Tooltip content={help}>
                <button
                  type="button"
                  aria-label="More information"
                  className="flex text-fg-3 transition-colors hover:text-fg-1 [&_svg]:size-4"
                >
                  <Info />
                </button>
              </Tooltip>
            )}
          </div>
          {subtitle && (
            <p className="mt-0.5 text-body-sm text-fg-3">{subtitle}</p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className={cn(flush ? "mt-3" : "p-5 pt-3", bodyClassName)}>
        {children}
      </div>
    </Card>
  );
}
